import React, { useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Radio,
  LinearProgress,
  Alert,
} from "@mui/material";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { VARIANTS, SIZES, ALIGN } from "../../../common/constants/commonConstants";
import { PAYMENT_STRINGS, PAYMENT_ERRORS, PAYMENT_LABELS } from "../../../common/constants/customerModule/paymentConstants";
import {
  getcustomerbycustomerid,
  getcustomerprofilebylastname,
} from "../../../services/customerinfoModule/profileServices";
import PaymentConfirmModal from "./PaymentConfirmModal";

const SEARCH_FILTERS = {
  CUSTOMER_ID: "customerId",
  LAST_NAME: "lastName",
};

const AddPayment = ({ onBack }) => {
  const [searchFilter, setSearchFilter] = useState(SEARCH_FILTERS.CUSTOMER_ID);
  const [searchText, setSearchText] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const canProceed = Boolean(selectedCustomerId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("now"); // 'now' or 'schedule'

  const getPlaceholder = () => {
    switch (searchFilter) {
      case SEARCH_FILTERS.CUSTOMER_ID:
        return "Search by Customer ID";
      case SEARCH_FILTERS.LAST_NAME:
        return "Search by Last Name";
      default:
        return "Search";
    }
  };

  const handleSearch = async () => {
    if (!searchText.trim()) {
      setError("Please enter search text.");
      return;
    }
    setError("");
    setIsLoading(true);
    setCustomers([]);
    setSelectedCustomerId(null);
    try {
      let data = [];
      if (searchFilter === SEARCH_FILTERS.CUSTOMER_ID) {
        data = await getcustomerbycustomerid({ customerid: searchText.trim() });
      } else {
        data = await getcustomerprofilebylastname({
          customerlastname: searchText.trim(),
        });
      }
      setCustomers(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length === 1) {
        setSelectedCustomerId(data[0].customerid);
      }
    } catch (err) {
      setError("Search failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 1 }} aria-label="back">
                <ArrowCircleLeftOutlinedIcon />
              </IconButton>
              <Typography variant="h6">
                {PAYMENT_STRINGS.ADD_PAYMENT}
              </Typography>
            </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={3}>
          <FormControl
            fullWidth
            size={SIZES.SMALL}
            variant={VARIANTS.OUTLINED}
          >
            <InputLabel>Search by</InputLabel>
            <Select
              value={searchFilter}
              label="Search by"
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setSearchText("");
              }}
            >
              <MenuItem value={SEARCH_FILTERS.CUSTOMER_ID}>{PAYMENT_STRINGS.CUSTOMER_ID}</MenuItem>
              <MenuItem value={SEARCH_FILTERS.LAST_NAME}>{PAYMENT_STRINGS.CUSTOMER}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={7}>
          <TextField
            fullWidth
            size={SIZES.SMALL}
            variant={VARIANTS.OUTLINED}
            value={searchText}
            placeholder={getPlaceholder()}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Grid>
        <Grid size={2}>
          <Button
            fullWidth
            variant={VARIANTS.CONTAINED}
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            {PAYMENT_STRINGS.SEARCH_BUTTON}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {!isLoading &&  (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>{PAYMENT_STRINGS.CUSTOMER_ID}</TableCell>
                <TableCell>{PAYMENT_LABELS.FIRST_NAME}</TableCell>
                <TableCell>{PAYMENT_LABELS.LAST_NAME}</TableCell>
                <TableCell>{PAYMENT_LABELS.EMAIL}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.length > 0 && customers.map((c, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => setSelectedCustomerId(c.customerid)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={selectedCustomerId === c.customerid}
                      onChange={() => setSelectedCustomerId(c.customerid)}
                    />
                  </TableCell>
                  <TableCell>{c.customerid || "-"}</TableCell>
                  <TableCell>{c.firstname || "-"}</TableCell>
                  <TableCell>{c.lastname || "-"}</TableCell>
                  <TableCell>{c.emailaddress || "-"}</TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align={ALIGN.CENTER}>
                    {PAYMENT_STRINGS.NO_RECORDS_FOUND}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && canProceed && (
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
            variant={VARIANTS.CONTAINED}
            color="primary"
            disabled={!canProceed}
            size="small"
            onClick={() => { setConfirmMode('now'); setConfirmOpen(true); }}
          >
            {PAYMENT_STRINGS.TAKE_PAYMENT_NOW}
          </Button>
            <Button
            variant={VARIANTS.OUTLINED}
            color="primary"
            disabled={!canProceed}
            size="small"
            onClick={() => { setConfirmMode('schedule'); setConfirmOpen(true); }}
          >
            {PAYMENT_STRINGS.SCHEDULE_PAYMENT}
          </Button>
        </Box>
      )}

      {/* Confirmation modal */}
      {confirmOpen && (
        <PaymentConfirmModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          selectedGuid={selectedCustomerId}
          mode={confirmMode}
        />
      )}

      {/* TODO: render payment form fields that use selectedGuid */}
    </Box>
  );
};

export default AddPayment;
